/* eslint-disable react/boolean-prop-naming */
/* eslint-disable react/jsx-handler-names */
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { DELIVERY_METHOD } from "../../../constants/common/common";
import { setMultiProductDateRange } from "../../../store/slices/common/shippingDateAndSlotSelection";
import { getMultiOrderDateRange } from "../../../utils/pdp/common";
import ButtonComponent from "../../atoms/button";
import CustomImage from "../../atoms/customImage";
import Toast from "../../atoms/toast";
import Typography from "../../atoms/typography";
import Portal from "../Portal";
import { CONTINUE } from "../../../constants/pageConstants/loginConstants";
import { constants, serenadeProductNotification } from "../../../constants/pageConstants/pdpConstants";

/**
 * SerenadeProduct
 *
 * This component handles multi-order notifications and user actions for selecting delivery dates and displaying relevant toast messages.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.multiOrderProductChange - Indicates if the multi-order product change modal should be displayed.
 * @param {boolean} props.isMultiOrderProduct - Determines if the current product is a multi-order product.
 * @param {boolean} props.nonDeliverableToast - Determines if the non-deliverable toast message should be displayed.
 * @param {Object} props.multiOrderProductDetails - Details about the multi-order product, including delivery days.
 * @param {function} props.setCurrentComponent - Function to set the current component view.
 * @param {function} props.setNonDeliverableToast - Function to toggle the non-deliverable toast visibility.
 * @param {string} props.selectedDate - The currently selected date for the delivery.
 */
const SerenadeProduct = ({
  multiOrderProductChange,
  isMultiOrderProduct,
  nonDeliverableToast,
  multiOrderProductDetails,
  setCurrentComponent,
  setNonDeliverableToast,
  selectedDate,
}) => {
  const dispatch = useDispatch();
  return (
    <>
      {multiOrderProductChange && (
        <Portal rootId="multiProduct" renderOnDidMount={true}>
          <div className="absolute bottom-0 w-[100%] p-16 md:static md:w-500 md:p-0 md:px-20">
            <div className="mb-16 flex items-start gap-4 rounded-4 p-4">
              <CustomImage
                src="/icons/info-circle-blue.svg"
                width={16}
                height={16}
                alt="delivery info"
                sizes="10vw"
                isPriority={false}
                className="mt-4"
              />
              <Typography Tag="p" classes="text-12 font-400 leading-20 text-black-100 font-inter font-normal">
                {serenadeProductNotification(multiOrderProductDetails)}
              </Typography>
            </div>
            <ButtonComponent
              size="small"
              classes="text-base flex justify-center md:mb-16"
              buttonVariant="filled"
              label={CONTINUE}
              data_testid="Continue"
              onClick={() => {
                setCurrentComponent(DELIVERY_METHOD);
                dispatch(setMultiProductDateRange(getMultiOrderDateRange(selectedDate, multiOrderProductDetails)));
              }}
              isRemoveExtraPadding={true}
            />
          </div>
        </Portal>
      )}
      {isMultiOrderProduct && nonDeliverableToast && (
        <div className="fixed bottom-[100px] left-1/2 z-20 flex w-full max-w-md -translate-x-1/2 transform items-center justify-center md:bottom-[30px]">
          <Toast
            variant="info"
            isToast={nonDeliverableToast}
            header={constants.DELIVERY_NOT_AVAILABLE}
            duration={4000}
            onToastChange={() => setNonDeliverableToast(false)}
          />
        </div>
      )}
    </>
  );
};

SerenadeProduct.propTypes = {
  multiOrderProductChange: PropTypes.bool.isRequired,
  isMultiOrderProduct: PropTypes.bool.isRequired,
  nonDeliverableToast: PropTypes.bool.isRequired,
  multiOrderProductDetails: PropTypes.shape({
    noOfDays: PropTypes.number,
  }).isRequired,
  setCurrentComponent: PropTypes.func.isRequired,
  setNonDeliverableToast: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
};

export default SerenadeProduct;
